import { Card, Badge } from "react-bootstrap";
import { useShoppingCart } from "../context/ShoppingCartContext";
import { useNavigate } from "react-router-dom";
import { Clock, GeoAlt, StarFill } from "react-bootstrap-icons";
import { useStoreRatingStats } from "../lib/query/queries/useStoreQueries";
import { useServiceAvailability } from "../context/ServiceAvailabilityContext";

export function StoreItem({ id, store_name, imgSrc, location, store_category, tab }) {
  const navigate = useNavigate();
  const { isOpen: isServiceOpen } = useServiceAvailability();
  // Same query key as StoreInfo.jsx/LiquorStoreInfo.jsx's rating stats query
  // (see queryKeys.ratings.stats) — cards on a list share one cached fetch
  // per store instead of each issuing its own request.
  const { data: ratingStats = { average_rating: 0, total_no_ratings: 0 } } =
    useStoreRatingStats(id);

  const formatLocation = (loc) => {
    if (!loc) return "Location not available";
    if (typeof loc === 'string') return loc;
    if (typeof loc === 'object') {
      return `${loc.latitude.toFixed(4)}, ${loc.longitude.toFixed(4)}`;
    }
    return "Invalid location";
  };

  const getCategoryIcon = (cat) => {
    switch (cat.toLowerCase()) {
      case 'fast-food':
        return '🍔';
      case 'liquor':
        return '🍷';
      case 'restaurant':
        return '🍽️';
      case 'cafe':
        return '☕';
      default:
        return '🏪';
    }
  };

  const handleCardClick = () => {
    if (tab === "fast-food") {
      navigate(`/stores/${id}`);
    } else {
      navigate(`/stores/liquor/${id}`);
    }
  };

  return (
    <Card 
      className="h-100 shadow-sm border-0" 
      onClick={handleCardClick}
      style={{ 
        cursor: "pointer",
        transition: "transform 0.2s ease-in-out",
      }}
      onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
      onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
    >
      <div className="position-relative">
        <Card.Img
          variant="top"
          src={imgSrc}
          height="200px"
          style={{ 
            objectFit: "contain",
            borderTopLeftRadius: "0.5rem",
            borderTopRightRadius: "0.5rem"
          }}
        />
        <div className="position-absolute top-0 end-0 d-flex gap-2 m-2">
          <Badge 
            bg="primary" 
            className="d-flex align-items-center gap-1 px-2 py-1"
            style={{ 
              fontSize: "0.8rem",
              fontWeight: "500",
              borderRadius: "0.5rem",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              backdropFilter: "blur(4px)",
              backgroundColor: "rgba(13, 110, 253, 0.9)",
            }}
          >
            <span style={{ fontSize: "1rem" }}>{getCategoryIcon(store_category)}</span>
            <span>{store_category}</span>
          </Badge>
          <Badge 
            bg="warning" 
            text="dark"
            className="d-flex align-items-center gap-1 px-2 py-1"
            style={{ 
              fontSize: "0.8rem",
              fontWeight: "500",
              borderRadius: "0.5rem",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              backdropFilter: "blur(4px)",
              backgroundColor: "rgba(255, 193, 7, 0.9)",
            }}
          >
            <StarFill size={12} />
            <span>{ratingStats.average_rating.toFixed(1)}</span>
          </Badge>
        </div>
      </div>
      
      <Card.Body className="d-flex flex-column p-3">
        <Card.Title className="d-flex justify-content-between align-items-baseline mb-3">
          <span className="fs-4 fw-bold text-truncate" style={{ color: '#2c3e50' }}>{store_name}</span>
        </Card.Title>
        
        <div className="mt-auto">
          <div className="d-flex align-items-center mb-2">
            <GeoAlt size={16} className="me-2" style={{ color: '#6c757d' }} />
            <small className="text-truncate text-muted" style={{ 
              color: '#495057',
              fontSize: '0.95rem',
              fontWeight: '500'
            }}>{formatLocation(location)}</small>
          </div>
          
          {/* Reflects the Teco-wide service_availability config that gates
              checkout, not this store's own hours. */}
          <div className="d-flex align-items-center">
            <Clock size={16} className="me-2" style={{ color: isServiceOpen ? '#6c757d' : '#FF9500' }} />
            <small style={{
              color: isServiceOpen ? '#28a745' : '#FF9500',
              fontSize: '0.95rem',
              fontWeight: '500'
            }}>{isServiceOpen ? 'Open now' : 'Closed'}</small>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
